import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Brain, Search, TrendingUp, Package, Building2, AlertTriangle, CheckCircle,
  XCircle, ArrowRight, Sparkles, BarChart3, Target, RefreshCw, Lightbulb,
  Truck, MapPin, Route, Clock, Navigation, Play, Save, Map, FileDown, Download
} from 'lucide-react';
import { toast } from 'sonner';

const CRITICIDADE_COLORS = {
  "Crítica": "bg-red-500",
  "Alta": "bg-orange-500",
  "Média": "bg-yellow-500",
  "Baixa": "bg-green-500"
};

const IMPACTO_COLORS = {
  "Alto": "bg-emerald-500",
  "Médio": "bg-blue-500",
  "Baixo": "bg-slate-500"
};

const VEICULOS = [
  { value: "carro", label: "Carro", icon: "🚗" },
  { value: "furgao", label: "Furgão", icon: "🚐" },
  { value: "camiao", label: "Camião", icon: "🚛" }
];

const ARMAZENS = [
  { value: "Lisboa", label: "Lisboa" },
  { value: "Porto", label: "Porto" },
  { value: "Coimbra", label: "Coimbra" },
  { value: "Faro", label: "Faro" },
  { value: "Braga", label: "Braga" }
];

export default function AnaliseInteligentePage() {
  const [activeTab, setActiveTab] = useState('matching');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Matching state
  const [matchingResults, setMatchingResults] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchDetailOpen, setMatchDetailOpen] = useState(false);
  const [alocandoMatch, setAlocandoMatch] = useState(false);
  const [alocacaoModalOpen, setAlocacaoModalOpen] = useState(false);
  const [alocacaoQuantidade, setAlocacaoQuantidade] = useState(0);
  
  // Gaps state
  const [gaps, setGaps] = useState([]);
  
  // Sugestões state
  const [sugestoes, setSugestoes] = useState([]);
  const [scoreMinimo, setScoreMinimo] = useState(50);
  
  // Rotas state
  const [alocacoesPendentes, setAlocacoesPendentes] = useState([]);
  const [alocacoesSelecionadas, setAlocacoesSelecionadas] = useState([]);
  const [rotaOtimizada, setRotaOtimizada] = useState(null);
  const [rotasSugeridas, setRotasSugeridas] = useState(null);
  const [rotaConfig, setRotaConfig] = useState({
    armazem: "Lisboa",
    veiculo: "furgao",
    maxParadas: 10
  });
  const [rotasSalvas, setRotasSalvas] = useState([]);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    instituicao_id: '',
    tipo_recurso_id: '',
    prioridade: '',
    armazem: ''
  });

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'rotas') {
      carregarAlocacoesPendentes();
      carregarRotasSalvas();
    }
  }, [activeTab]);

  const loadStats = async () => {
    try {
      const res = await apiClient.get('/analise/estatisticas');
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const executarAnaliseMatching = async () => {
    try {
      setLoading(true);
      toast.info('A analisar necessidades vs stocks...');
      
      const fd = new FormData();
      if (filtros.instituicao_id) fd.append('instituicao_id', filtros.instituicao_id);
      if (filtros.tipo_recurso_id) fd.append('tipo_recurso_id', filtros.tipo_recurso_id);
      if (filtros.prioridade && filtros.prioridade !== 'all') fd.append('prioridade', filtros.prioridade);
      
      const res = await apiClient.post('/analise/matching-global', fd);
      setMatchingResults(res.data);
      toast.success(`Análise completa: ${res.data.estatisticas.total_analisadas} necessidades`);
    } catch (error) {
      toast.error('Erro na análise');
    } finally {
      setLoading(false);
    }
  };

  const iniciarAlocacao = (match) => {
    setSelectedMatch(match);
    setAlocacaoQuantidade(match.quantidade || match.quantidade_necessaria || 0);
    setAlocacaoModalOpen(true);
  };

  const confirmarAlocacao = async () => {
    if (!selectedMatch || !alocacaoQuantidade) return;
    
    setAlocandoMatch(true);
    try {
      const res = await apiClient.post('/analise/matching/alocar', {
        necessidade_id: selectedMatch.necessidade_id,
        match_index: 0,
        quantidade: alocacaoQuantidade
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setAlocacaoModalOpen(false);
        executarAnaliseMatching(); // Refresh results
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alocar');
    } finally {
      setAlocandoMatch(false);
    }
  };

  const carregarGaps = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.instituicao_id) params.append('instituicao_id', filtros.instituicao_id);
      if (filtros.tipo_recurso_id) params.append('tipo_recurso_id', filtros.tipo_recurso_id);
      
      const res = await apiClient.get(`/analise/gaps?${params.toString()}`);
      setGaps(res.data.gaps || []);
      toast.success(`${res.data.total_gaps} gaps identificados`);
    } catch (error) {
      toast.error('Erro ao carregar gaps');
    } finally {
      setLoading(false);
    }
  };

  const carregarSugestoes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('score_minimo', scoreMinimo);
      if (filtros.armazem && filtros.armazem !== 'all') params.append('armazem', filtros.armazem);
      if (filtros.tipo_recurso_id) params.append('tipo_recurso_id', filtros.tipo_recurso_id);
      
      const res = await apiClient.get(`/analise/sugestoes-proativas?${params.toString()}`);
      setSugestoes(res.data.sugestoes || []);
      toast.success(`${res.data.total} sugestões geradas`);
    } catch (error) {
      toast.error('Erro ao gerar sugestões');
    } finally {
      setLoading(false);
    }
  };

  const aceitarSugestao = async (sugestao) => {
    try {
      const fd = new FormData();
      fd.append('sugestao_id', sugestao.id);
      fd.append('stock_id', sugestao.stock_id);
      fd.append('instituicao_id', sugestao.instituicao_id);
      fd.append('quantidade', sugestao.quantidade_sugerida);
      
      await apiClient.post('/analise/aceitar-sugestao', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Sugestão aceite e alocação criada');
      carregarSugestoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aceitar sugestão');
    }
  };

  // ===== FUNÇÕES DE ROTAS =====
  
  const carregarAlocacoesPendentes = async () => {
    try {
      const res = await apiClient.get('/analise/alocacoes-pendentes');
      setAlocacoesPendentes(res.data.alocacoes || []);
    } catch (error) {
      console.error('Erro ao carregar alocações:', error);
    }
  };

  const carregarRotasSalvas = async () => {
    try {
      const res = await apiClient.get('/analise/rotas');
      setRotasSalvas(res.data.rotas || []);
    } catch (error) {
      console.error('Erro ao carregar rotas salvas:', error);
    }
  };

  const toggleAlocacao = (alocacaoId) => {
    setAlocacoesSelecionadas(prev => {
      if (prev.includes(alocacaoId)) {
        return prev.filter(id => id !== alocacaoId);
      } else {
        return [...prev, alocacaoId];
      }
    });
  };

  const selecionarTodas = () => {
    if (alocacoesSelecionadas.length === alocacoesPendentes.length) {
      setAlocacoesSelecionadas([]);
    } else {
      setAlocacoesSelecionadas(alocacoesPendentes.map(a => a.id));
    }
  };

  const otimizarRotaSelecionada = async () => {
    if (alocacoesSelecionadas.length === 0) {
      toast.error('Selecione pelo menos uma alocação');
      return;
    }
    
    try {
      setLoading(true);
      toast.info('A otimizar rota...');
      
      const fd = new FormData();
      fd.append('alocacoes_ids', alocacoesSelecionadas.join(','));
      fd.append('armazem_origem', rotaConfig.armazem);
      fd.append('tipo_veiculo', rotaConfig.veiculo);
      fd.append('max_paradas', rotaConfig.maxParadas);
      
      const res = await apiClient.post('/analise/otimizar-rota', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRotaOtimizada(res.data);
      toast.success(`Rota otimizada: ${res.data.estatisticas?.total_paradas || 0} paradas`);
    } catch (error) {
      console.error('Erro ao otimizar rota:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Erro ao otimizar rota');
      toast.error(msg || 'Erro ao otimizar rota');
    } finally {
      setLoading(false);
    }
  };

  const sugerirRotasDia = async () => {
    try {
      setLoading(true);
      toast.info('A gerar sugestões de rotas...');
      
      const params = new URLSearchParams();
      params.append('armazem', rotaConfig.armazem);
      params.append('max_por_rota', rotaConfig.maxParadas);
      
      const res = await apiClient.get(`/analise/sugerir-rotas?${params.toString()}`);
      setRotasSugeridas(res.data);
      toast.success(`${res.data.total_rotas} rotas sugeridas`);
    } catch (error) {
      toast.error('Erro ao sugerir rotas');
    } finally {
      setLoading(false);
    }
  };

  const salvarRota = async (rota) => {
    try {
      const fd = new FormData();
      fd.append('rota_json', JSON.stringify(rota));
      
      await apiClient.post('/analise/salvar-rota', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Rota salva com sucesso');
      carregarRotasSalvas();
    } catch (error) {
      toast.error('Erro ao salvar rota');
    }
  };

  const atualizarStatusRota = async (rotaId, novoStatus) => {
    try {
      const fd = new FormData();
      fd.append('status', novoStatus);
      
      await apiClient.put(`/analise/rotas/${rotaId}/status`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Status atualizado: ${novoStatus}`);
      carregarRotasSalvas();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  // ===== FUNÇÕES DE EXPORTAÇÃO PDF =====
  
  const exportarPDF = async (tipo, params = {}) => {
    try {
      toast.info(`A gerar PDF de ${tipo}...`);
      
      let url = `/analise/export/${tipo}/pdf`;
      const queryParams = new URLSearchParams(params).toString();
      if (queryParams) url += `?${queryParams}`;
      
      const response = await apiClient.get(url, { responseType: 'blob' });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `relatorio_${tipo}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const exportarMatchingPDF = () => {
    const params = {};
    if (filtros.prioridade && filtros.prioridade !== 'all') {
      params.prioridade = filtros.prioridade;
    }
    exportarPDF('matching', params);
  };

  const exportarGapsPDF = () => exportarPDF('gaps');

  const exportarSugestoesPDF = () => {
    exportarPDF('sugestoes', { score_minimo: scoreMinimo });
  };

  const exportarRotasPDF = () => {
    const params = { armazem: rotaConfig.armazem };
    exportarPDF('rotas', params);
  };

  const exportarRelatorioCompleto = () => exportarPDF('completo');

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusColor = (status) => {
    const colors = {
      "Planeada": "bg-blue-100 text-blue-800",
      "Em Execução": "bg-yellow-100 text-yellow-800",
      "Concluída": "bg-green-100 text-green-800",
      "Cancelada": "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6" data-testid="analise-inteligente-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-600" />
            Análise Inteligente
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Sistema de matching necessidades vs stocks com IA e otimização de rotas
          </p>
        </div>
        <Button
          variant="outline"
          onClick={exportarRelatorioCompleto}
          className="flex items-center gap-2"
          data-testid="btn-export-completo"
        >
          <FileDown className="w-4 h-4" />
          Exportar Tudo (PDF)
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Stocks Disponíveis</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.stocks?.disponiveis || 0}
                  </p>
                </div>
                <Package className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Necessidades Pendentes</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.necessidades?.pendentes || 0}
                  </p>
                </div>
                <Target className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Qtd. Disponível</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.stocks?.quantidade_disponivel?.toLocaleString() || 0}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Taxa Cobertura</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {stats.cobertura?.taxa || 0}%
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="matching" className="flex items-center gap-2">
            <Search className="w-4 h-4" /> Matching
          </TabsTrigger>
          <TabsTrigger value="gaps" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Gaps
          </TabsTrigger>
          <TabsTrigger value="sugestoes" className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4" /> Sugestões
          </TabsTrigger>
          <TabsTrigger value="rotas" className="flex items-center gap-2" data-testid="tab-rotas">
            <Truck className="w-4 h-4" /> Rotas
          </TabsTrigger>
        </TabsList>

        {/* TAB MATCHING */}
        <TabsContent value="matching" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Análise de Matching Necessidades vs Stocks
              </CardTitle>
              <CardDescription>
                Encontra os melhores stocks para cada necessidade pendente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label>Prioridade</Label>
                  <Select 
                    value={filtros.prioridade} 
                    onValueChange={v => setFiltros(p => ({ ...p, prioridade: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="Urgente">Urgente</SelectItem>
                      <SelectItem value="Alta">Alta</SelectItem>
                      <SelectItem value="Média">Média</SelectItem>
                      <SelectItem value="Baixa">Baixa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={executarAnaliseMatching} 
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="btn-analisar-matching"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                  Analisar Agora
                </Button>
                <Button 
                  variant="outline"
                  onClick={exportarMatchingPDF}
                  disabled={!matchingResults}
                  data-testid="btn-export-matching-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>

              {/* Resultados Matching */}
              {matchingResults && (
                <div className="space-y-4">
                  {/* Stats da análise */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                      <p className="text-2xl font-bold">{matchingResults.estatisticas.total_analisadas}</p>
                      <p className="text-xs text-slate-500">Analisadas</p>
                    </div>
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{matchingResults.estatisticas.com_match_perfeito}</p>
                      <p className="text-xs text-slate-500">Match Perfeito</p>
                    </div>
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-yellow-600">{matchingResults.estatisticas.com_match_aceitavel}</p>
                      <p className="text-xs text-slate-500">Match Aceitável</p>
                    </div>
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">{matchingResults.estatisticas.sem_match}</p>
                      <p className="text-xs text-slate-500">Sem Match</p>
                    </div>
                  </div>

                  {/* Tabela resultados */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bem / Recurso</TableHead>
                        <TableHead>Instituição</TableHead>
                        <TableHead className="text-center">Qtd.</TableHead>
                        <TableHead>Prioridade</TableHead>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead>Recomendação</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {matchingResults.resultados.slice(0, 20).map((r, idx) => (
                        <TableRow key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                {r.tipo_recurso_nome || r.recurso_nome || 'Recurso não especificado'}
                              </p>
                              <p className="text-xs text-slate-500">
                                {r.categoria || r.tipo_recurso || '-'}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{r.instituicao_nome || 'N/A'}</p>
                              <p className="text-xs text-slate-500">{r.distrito || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center font-mono">
                            {r.quantidade_necessaria || r.quantidade || 0}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              r.prioridade === 'Crítica' ? 'border-red-500 text-red-500' :
                              r.prioridade === 'Alta' ? 'border-orange-500 text-orange-500' :
                              r.prioridade === 'Média' ? 'border-yellow-500 text-yellow-600' :
                              'border-green-500 text-green-500'
                            }>
                              {r.prioridade || 'Normal'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`text-lg font-bold ${getScoreColor(r.top_score)}`}>
                              {r.top_score || 0}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              r.recomendacao === 'Alocar' ? 'bg-green-500' :
                              r.recomendacao === 'Avaliar' ? 'bg-yellow-500' : 'bg-red-500'
                            }>
                              {r.recomendacao}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {r.recomendacao === 'Alocar' && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="h-7 text-green-600 border-green-300 hover:bg-green-50"
                                  onClick={() => iniciarAlocacao(r)}
                                >
                                  <ArrowRight className="w-3 h-3 mr-1" />
                                  Alocar
                                </Button>
                              )}
                              {r.recomendacao === 'Avaliar' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7"
                                  onClick={() => iniciarAlocacao(r)}
                                >
                                  Ver Opções
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB GAPS */}
        <TabsContent value="gaps" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Mapa de Necessidades (Gaps)
              </CardTitle>
              <CardDescription>
                Identifica recursos em falta e prioriza aquisições
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button 
                  onClick={carregarGaps} 
                  disabled={loading}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="btn-identificar-gaps"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Identificar Gaps
                </Button>
                <Button 
                  variant="outline"
                  onClick={exportarGapsPDF}
                  data-testid="btn-export-gaps-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar PDF
                </Button>
              </div>

              {/* Lista de Gaps */}
              {gaps.length > 0 && (
                <div className="space-y-3">
                  {gaps.map((gap, idx) => (
                    <div 
                      key={idx}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{gap.tipo_recurso_nome}</h4>
                            <Badge className={CRITICIDADE_COLORS[gap.criticidade]}>
                              {gap.criticidade}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">Quantidade Faltante:</span>
                              <span className="ml-2 font-medium text-red-600">{gap.quantidade_faltante}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Instituições:</span>
                              <span className="ml-2 font-medium">{gap.num_instituicoes}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Prioridade Aquisição:</span>
                              <span className="ml-2 font-bold text-purple-600">{gap.prioridade_aquisicao}</span>
                            </div>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          Iniciar Aquisição
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {gaps.length === 0 && !loading && (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p>Nenhum gap identificado ou análise não executada</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB SUGESTÕES PROATIVAS */}
        <TabsContent value="sugestoes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                Sugestões Proativas de Distribuição
              </CardTitle>
              <CardDescription>
                Stocks que podem beneficiar instituições que não receberam apoio recentemente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="w-48">
                  <Label>Score Mínimo</Label>
                  <Input
                    type="number"
                    value={scoreMinimo}
                    onChange={e => setScoreMinimo(parseInt(e.target.value) || 50)}
                    min={0}
                    max={100}
                  />
                </div>
                <div className="flex-1">
                  <Label>Armazém</Label>
                  <Select 
                    value={filtros.armazem} 
                    onValueChange={v => setFiltros(p => ({ ...p, armazem: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="Lisboa">Lisboa</SelectItem>
                      <SelectItem value="Porto">Porto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={carregarSugestoes} 
                  disabled={loading}
                  className="bg-yellow-600 hover:bg-yellow-700"
                  data-testid="btn-gerar-sugestoes"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Gerar Sugestões
                </Button>
                <Button 
                  variant="outline"
                  onClick={exportarSugestoesPDF}
                  disabled={sugestoes.length === 0}
                  data-testid="btn-export-sugestoes-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>

              {/* Lista de Sugestões */}
              {sugestoes.length > 0 && (
                <div className="space-y-3">
                  {sugestoes.map((sug, idx) => (
                    <div 
                      key={idx}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Package className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">{sug.tipo_recurso_nome}</span>
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                            <Building2 className="w-4 h-4 text-green-500" />
                            <span className="font-medium">{sug.instituicao_nome}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm mb-2">
                            <Badge className={IMPACTO_COLORS[sug.impacto]}>{sug.impacto}</Badge>
                            <span className="text-slate-500">
                              Quantidade: <strong>{sug.quantidade_sugerida}</strong>
                            </span>
                            <span className="text-slate-500">
                              Dias sem apoio: <strong className="text-orange-600">{sug.dias_sem_apoio}</strong>
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{sug.justificacao}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className={`text-2xl font-bold ${getScoreColor(sug.score_match)}`}>
                            {sug.score_match}
                          </div>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => aceitarSugestao(sug)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Aceitar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {sugestoes.length === 0 && !loading && (
                <div className="text-center py-12 text-slate-500">
                  <Lightbulb className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Clique em "Gerar Sugestões" para obter recomendações proativas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB OTIMIZAÇÃO DE ROTAS */}
        <TabsContent value="rotas" className="space-y-4" data-testid="tab-content-rotas">
          {/* Configuração da Rota */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Route className="w-5 h-5 text-blue-500" />
                Otimização de Rotas de Entrega
              </CardTitle>
              <CardDescription>
                Optimize as entregas selecionando alocações e configurando a rota
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Configurações */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Armazém de Origem</Label>
                  <Select 
                    value={rotaConfig.armazem} 
                    onValueChange={v => setRotaConfig(p => ({ ...p, armazem: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ARMAZENS.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de Veículo</Label>
                  <Select 
                    value={rotaConfig.veiculo} 
                    onValueChange={v => setRotaConfig(p => ({ ...p, veiculo: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VEICULOS.map(v => (
                        <SelectItem key={v.value} value={v.value}>
                          {v.icon} {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Máx. Paradas</Label>
                  <Input
                    type="number"
                    value={rotaConfig.maxParadas}
                    onChange={e => setRotaConfig(p => ({ ...p, maxParadas: parseInt(e.target.value) || 10 }))}
                    min={1}
                    max={20}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button 
                    onClick={sugerirRotasDia} 
                    disabled={loading}
                    variant="outline"
                    className="flex-1"
                    data-testid="btn-sugerir-rotas"
                  >
                    <Map className="w-4 h-4 mr-2" />
                    Sugerir Rotas
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={exportarRotasPDF}
                    data-testid="btn-export-rotas-pdf"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Alocações Pendentes */}
              <div className="border rounded-lg">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 flex justify-between items-center border-b">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    <span className="font-medium">Alocações Pendentes</span>
                    <Badge variant="outline">{alocacoesPendentes.length}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={selecionarTodas}
                    >
                      {alocacoesSelecionadas.length === alocacoesPendentes.length ? 'Desselecionar' : 'Selecionar'} Todas
                    </Button>
                    <Button 
                      onClick={otimizarRotaSelecionada} 
                      disabled={loading || alocacoesSelecionadas.length === 0}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="btn-otimizar-rota"
                    >
                      {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Navigation className="w-4 h-4 mr-2" />}
                      Otimizar ({alocacoesSelecionadas.length})
                    </Button>
                  </div>
                </div>
                
                {alocacoesPendentes.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Instituição</TableHead>
                          <TableHead>Distrito</TableHead>
                          <TableHead>Recurso</TableHead>
                          <TableHead className="text-center">Qtd</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {alocacoesPendentes.map((aloc) => (
                          <TableRow 
                            key={aloc.id}
                            className={alocacoesSelecionadas.includes(aloc.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                          >
                            <TableCell>
                              <Checkbox
                                checked={alocacoesSelecionadas.includes(aloc.id)}
                                onCheckedChange={() => toggleAlocacao(aloc.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{aloc.instituicao_nome}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-slate-400" />
                                {aloc.distrito}
                              </div>
                            </TableCell>
                            <TableCell>{aloc.tipo_recurso_nome}</TableCell>
                            <TableCell className="text-center">{aloc.quantidade}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <Truck className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Nenhuma alocação pendente de entrega</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rota Otimizada */}
          {rotaOtimizada && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-green-500" />
                  Rota Otimizada
                </CardTitle>
                <div className="flex gap-4 mt-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {rotaOtimizada.estatisticas?.total_paradas || 0} paradas
                  </Badge>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Route className="w-3 h-3" />
                    {rotaOtimizada.estatisticas?.distancia_total_km || 0} km
                  </Badge>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {rotaOtimizada.estatisticas?.tempo_estimado_formatado || '0h 0min'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Origem */}
                  <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      O
                    </div>
                    <div>
                      <p className="font-medium">{rotaOtimizada.origem?.nome}</p>
                      <p className="text-sm text-slate-500">Ponto de partida</p>
                    </div>
                  </div>

                  {/* Paradas */}
                  {rotaOtimizada.rota?.map((parada, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                        {parada.ordem}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{parada.instituicao_nome}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {parada.distrito}
                          </span>
                          <span className="flex items-center gap-1">
                            <Route className="w-3 h-3" /> +{parada.distancia_anterior_km} km
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> +{parada.tempo_viagem_min} min
                          </span>
                        </div>
                      </div>
                      <Badge variant="outline">{parada.quantidade_entregar} un.</Badge>
                    </div>
                  ))}

                  {/* Retorno */}
                  <div className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                      R
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">Retorno ao Armazém</p>
                      <p className="text-sm text-slate-500">
                        +{rotaOtimizada.retorno?.distancia_km} km | +{rotaOtimizada.retorno?.tempo_min} min
                      </p>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => salvarRota(rotaOtimizada)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Save className="w-4 h-4 mr-2" /> Salvar Rota
                    </Button>
                    <Button variant="outline">
                      <Play className="w-4 h-4 mr-2" /> Iniciar Entrega
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rotas Sugeridas */}
          {rotasSugeridas && rotasSugeridas.rotas?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map className="w-5 h-5 text-purple-500" />
                  Rotas Sugeridas do Dia
                </CardTitle>
                <CardDescription>
                  {rotasSugeridas.total_rotas} rotas para {rotasSugeridas.total_entregas} entregas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {rotasSugeridas.rotas.map((rota, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold">{rota.nome}</h4>
                          <Badge variant="outline" className="mt-1">{rota.regiao}</Badge>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setRotaOtimizada(rota)}
                        >
                          Ver Detalhes
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                          <p className="text-lg font-bold">{rota.estatisticas?.total_paradas}</p>
                          <p className="text-xs text-slate-500">Paradas</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                          <p className="text-lg font-bold">{rota.estatisticas?.distancia_total_km}</p>
                          <p className="text-xs text-slate-500">Km</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                          <p className="text-lg font-bold">{rota.estatisticas?.tempo_estimado_formatado}</p>
                          <p className="text-xs text-slate-500">Tempo</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rotas Salvas */}
          {rotasSalvas.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Save className="w-5 h-5 text-slate-500" />
                  Rotas Salvas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Região</TableHead>
                      <TableHead>Paradas</TableHead>
                      <TableHead>Distância</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rotasSalvas.map((rota) => (
                      <TableRow key={rota.id}>
                        <TableCell className="font-medium">{rota.nome || `Rota ${rota.id?.slice(0, 8)}`}</TableCell>
                        <TableCell>{rota.regiao || '-'}</TableCell>
                        <TableCell>{rota.estatisticas?.total_paradas || 0}</TableCell>
                        <TableCell>{rota.estatisticas?.distancia_total_km || 0} km</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(rota.status)}>
                            {rota.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {rota.status === 'Planeada' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => atualizarStatusRota(rota.id, 'Em Execução')}
                              >
                                <Play className="w-3 h-3" />
                              </Button>
                            )}
                            {rota.status === 'Em Execução' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => atualizarStatusRota(rota.id, 'Concluída')}
                              >
                                <CheckCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal de Alocação */}
      <Dialog open={alocacaoModalOpen} onOpenChange={setAlocacaoModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-500" />
              Confirmar Alocação
            </DialogTitle>
            <DialogDescription>
              Alocar recursos para a instituição
            </DialogDescription>
          </DialogHeader>

          {selectedMatch && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Recurso:</span>
                  <span className="font-medium">{selectedMatch.tipo_recurso_nome || selectedMatch.recurso_nome}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Instituição:</span>
                  <span className="font-medium">{selectedMatch.instituicao_nome}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Necessidade:</span>
                  <span className="font-medium">{selectedMatch.quantidade || selectedMatch.quantidade_necessaria} unidades</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Score Match:</span>
                  <span className={`font-bold ${getScoreColor(selectedMatch.top_score)}`}>
                    {selectedMatch.top_score}%
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Quantidade a alocar</Label>
                <Input
                  type="number"
                  min="1"
                  max={selectedMatch.quantidade || selectedMatch.quantidade_necessaria}
                  value={alocacaoQuantidade}
                  onChange={(e) => setAlocacaoQuantidade(parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAlocacaoModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={confirmarAlocacao}
              disabled={alocandoMatch || !alocacaoQuantidade}
              className="bg-green-600 hover:bg-green-700"
            >
              {alocandoMatch ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  A alocar...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Alocação
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
